// Generated by GitHub Copilot
import React, { useState, useEffect } from "react";
import { Alert, Button, Card, Badge } from "react-bootstrap";
import { Copy, CheckCircle, AlertTriangle } from "react-feather";
import "./ErrorContents.css";

/**
 * Enhanced ErrorContents component to display comprehensive error information
 * for easier troubleshooting and bug reporting
 */
const ErrorContents = ({ error, errorInfo, resetErrorBoundary }) => {
  const [copied, setCopied] = useState(false);
  const [errorId] = useState(
    () => `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  );
  const [timestamp] = useState(new Date().toISOString());

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  // Generate comprehensive error report
  const generateErrorReport = () => {
    const report = {
      errorId,
      timestamp,
      url: window.location.href,
      userAgent: navigator.userAgent,
      error: {
        message:
          error?.message ||
          error?.toString() ||
          String(error) ||
          "Unknown error",
        name: error?.name || "Error",
        stack: error?.stack || "No stack trace available",
      },
      componentStack:
        errorInfo?.componentStack || "No component stack available",
      browserInfo: {
        language: navigator.language,
        platform: navigator.platform,
        cookiesEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
      },
      screenInfo: {
        width: window.screen.width,
        height: window.screen.height,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
      },
      viewportInfo: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    };

    return report;
  };

  // Copy error details to clipboard
  const copyToClipboard = async () => {
    const report = generateErrorReport();
    const reportText = `
=== ERROR REPORT ===
Error ID: ${report.errorId}
Timestamp: ${report.timestamp}
URL: ${report.url}

=== ERROR DETAILS ===
Type: ${report.error.name}
Message: ${report.error.message}

=== STACK TRACE ===
${report.error.stack}

=== COMPONENT STACK ===
${report.componentStack}

=== BROWSER INFO ===
User Agent: ${report.userAgent}
Language: ${report.browserInfo.language}
Platform: ${report.browserInfo.platform}
Online: ${report.browserInfo.onLine}

=== SCREEN INFO ===
Screen: ${report.screenInfo.width}x${report.screenInfo.height}
Viewport: ${report.viewportInfo.width}x${report.viewportInfo.height}
    `.trim();

    try {
      await navigator.clipboard.writeText(reportText);
      setCopied(true);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = reportText;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
      } catch (e) {
        console.error("Failed to copy:", e);
      }
      document.body.removeChild(textArea);
    }
  };

  const report = generateErrorReport();

  return (
    <div className="error-boundary-container">
      <Card className="error-card">
        <Card.Body>
          <div className="error-header">
            <AlertTriangle size={48} color="#dc3545" />
            <h2>Something Went Wrong</h2>
          </div>

          <Alert variant="danger" className="mb-4">
            <Alert.Heading>An unexpected error has occurred</Alert.Heading>
            <p>
              We're sorry for the inconvenience. The application encountered an
              error and couldn't continue. You can try the following:
            </p>
            <ul className="mb-0">
              <li>Click "Try Again" to reload the component</li>
              <li>Refresh the page (press F5 or Cmd+R)</li>
              <li>Clear your browser cache and reload</li>
              <li>
                If the problem persists, copy the error details below and
                contact support
              </li>
            </ul>
          </Alert>

          <div className="error-actions mb-4">
            {resetErrorBoundary && (
              <Button
                variant="primary"
                onClick={resetErrorBoundary}
                className="me-2"
              >
                Try Again
              </Button>
            )}
            <Button
              variant={copied ? "success" : "outline-primary"}
              onClick={copyToClipboard}
            >
              {copied ? (
                <>
                  <CheckCircle size={16} className="me-1" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={16} className="me-1" />
                  Copy Error Details
                </>
              )}
            </Button>
          </div>

          {/* Error ID Badge */}
          <div className="mb-3">
            <Badge bg="secondary" className="error-id-badge">
              Error ID: {errorId}
            </Badge>
            <Badge bg="info" className="ms-2">
              {new Date(timestamp).toLocaleString()}
            </Badge>
          </div>

          {/* Collapsible Error Details */}
          <Card className="mb-2">
            <Card.Header>
              <details>
                <summary className="error-summary">
                  <strong>Error Message</strong>
                </summary>
                <div className="error-detail-content">
                  <pre className="error-pre">
                    <strong>{report.error.name}:</strong> {report.error.message}
                  </pre>
                </div>
              </details>
            </Card.Header>
          </Card>

          <Card className="mb-2">
            <Card.Header>
              <details>
                <summary className="error-summary">
                  <strong>Stack Trace</strong>
                </summary>
                <div className="error-detail-content">
                  <pre className="error-pre">{report.error.stack}</pre>
                </div>
              </details>
            </Card.Header>
          </Card>

          {errorInfo && (
            <Card className="mb-2">
              <Card.Header>
                <details>
                  <summary className="error-summary">
                    <strong>Component Stack</strong>
                  </summary>
                  <div className="error-detail-content">
                    <pre className="error-pre">{report.componentStack}</pre>
                  </div>
                </details>
              </Card.Header>
            </Card>
          )}

          <Card className="mb-2">
            <Card.Header>
              <details>
                <summary className="error-summary">
                  <strong>Environment Information</strong>
                </summary>
                <div className="error-detail-content">
                  <table className="info-table">
                    <tbody>
                      <tr>
                        <td>
                          <strong>URL:</strong>
                        </td>
                        <td>{report.url}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Browser:</strong>
                        </td>
                        <td>{report.userAgent}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Platform:</strong>
                        </td>
                        <td>{report.browserInfo.platform}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Language:</strong>
                        </td>
                        <td>{report.browserInfo.language}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Online:</strong>
                        </td>
                        <td>{report.browserInfo.onLine ? "Yes" : "No"}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Screen:</strong>
                        </td>
                        <td>
                          {report.screenInfo.width}×{report.screenInfo.height}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Viewport:</strong>
                        </td>
                        <td>
                          {report.viewportInfo.width}×
                          {report.viewportInfo.height}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </details>
            </Card.Header>
          </Card>

          <Alert variant="info" className="mt-4 mb-0">
            <small>
              <strong>To report this error:</strong> Click "Copy Error Details"
              above and include the information in your bug report or support
              ticket. The Error ID will help developers track down the issue.
            </small>
          </Alert>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ErrorContents;
